import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import {
  FaPlus,
  FaSearch,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/hireDetailsListing";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const HireVehicleDetailsListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchActive =
    sessionStorage.getItem("BranchActive") ||
    sessionStorage.getItem("branchActive") ||
    "1";

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const [selectedBranchId, setSelectedBranchId] = useState(branchId || "");
  const [storeId, setStoreId] = useState("Select");
  const [vehicleType, setVehicleType] = useState("Select");
  const [hiredFrom, setHiredFrom] = useState("");
  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleRegNo, setVehicleRegNo] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rights, setRights] = useState({
    canAccess: true,
    canAdd: true,
    canDelete: true,
  });

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const normalizeValue = (val) => {
    if (!val || val === "Select") return "";
    return String(val);
  };

  const normalizeRow = (row) => ({
    ...row,
    vehicleDetId:
      row?.fld_vehicledetid ||
      row?.vehicledetid ||
      row?.VehicleDetId ||
      row?.VehicleDetID ||
      0,
    vehicleId: row?.fld_vehicleid || row?.vehicleid || row?.VehicleID || 0,
    vehicleCode:
      row?.fld_vehicle_code ||
      row?.vehicleCode ||
      row?.VehicleCode ||
      row?.["Vehicle Code"] ||
      "-",
    vehicleName:
      row?.fld_vehicle_name ||
      row?.vehicleName ||
      row?.VehicleName ||
      row?.["Vehicle Name"] ||
      "-",
    vehicleRegNo:
      row?.fld_vehicle_regno ||
      row?.vehicleRegNo ||
      row?.VehicleRegNo ||
      row?.["Vehicle Registration Number"] ||
      "-",
    vehicleSubtype:
      row?.fld_vehicle_subtypename ||
      row?.vehicleSubtype ||
      row?.VehicleSubtype ||
      row?.["Vehicle Subtype"] ||
      "-",
    hiredFrom:
      row?.fld_hiredfrom ||
      row?.hiredFrom ||
      row?.HiredFrom ||
      row?.["Hired From"] ||
      "-",
    basis:
      row?.fld_vehicle_basistype ||
      row?.fld_vehicle_basis ||
      row?.vehicleBasis ||
      row?.VehicleBasis ||
      row?.["Vehicle Basis"] ||
      "-",
    status:
      row?.Status ||
      row?.fld_vehicle_status ||
      row?.vehicleStatus ||
      row?.VehicleStatus ||
      row?.["Vehicle Status"] ||
      "Open",
  });

  const loadMeta = async (branchParam) => {
    try {
      const selected =
        branchType === "HO"
          ? branchParam || selectedBranchId || branchId
          : branchId;

      const r = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchId,
          loginUserId: userId,
          branchType,
          selectedBranchId: selected,
        },
        headers: commonHeaders,
      });

      if (!r.data?.success) throw new Error(r.data?.message || "Meta failed");

      const data = r.data?.data || {};
      setBranches(Array.isArray(data?.branches) ? data.branches : []);
      setStores(Array.isArray(data?.stores) ? data.stores : []);
      setVehicleTypes(Array.isArray(data?.vehicleTypes) ? data.vehicleTypes : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdown data!");
      setBranches([]);
      setStores([]);
      setVehicleTypes([]);
    }
  };

  const fetchList = async (mode) => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    setSelectedRows([]);
    setHasSearched(true);
    setCurrentPage(1);

    try {
      const selected =
        branchType === "HO" ? selectedBranchId || branchId : branchId;

      const r = await axios.get(`${API_BASE}/list`, {
        params: {
          mode,
          branchId,
          loginUserId: userId,
          branchType,
          selectedBranchId: selected,
          storeId: normalizeValue(storeId),
          vehicleType: normalizeValue(vehicleType),
          hiredFrom,
          vehicleCode,
          vehicleRegNo,
          vehicleName,
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
      if (data.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error(err);
      toast.error(
        mode === "all" ? "Failed to load view all list" : "Failed to load list"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadList = () => fetchList("search");
  const loadViewAll = () => fetchList("all");

  const onBranchChange = async (e) => {
    const val = e.target.value;
    setSelectedBranchId(val);
    setStoreId("Select");
    await loadMeta(val);
  };

  const totalRecords = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const pageStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, totalRecords);

  const pageIds = useMemo(
    () =>
      paginatedRows
        .map((r) => Number(r.vehicleDetId))
        .filter((id) => Number.isFinite(id) && id > 0),
    [paginatedRows]
  );

  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedRows.includes(id));

  const toggleRow = (id) => {
    if (!id) return;
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (allPageSelected) {
      setSelectedRows((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedRows((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const clearFilters = () => {
    setStoreId("Select");
    setVehicleType("Select");
    setHiredFrom("");
    setVehicleCode("");
    setVehicleRegNo("");
    setVehicleName("");
    setCurrentPage(1);
    toast.info("Filters cleared");
  };

  const onEdit = (row) => {
    navigate(
      `/vehicle/HireVehicle_DetailsEntry?vehicledetid=${row.vehicleDetId || ""}&vehicleid=${
        row.vehicleId || ""
      }&vehicleCode=${encodeURIComponent(row.vehicleCode || "")}`
    );
  };

  const onDeleteSelected = async () => {
    if (!rights.canDelete) {
      toast.error("You don't have delete rights.");
      return;
    }

    if (branchActive !== "1") {
      toast.error("Branch is inactive. Delete not allowed.");
      return;
    }

    const ids = selectedRows
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x) && x > 0);

    if (!ids.length) {
      toast.warn("Please select at least one record to delete.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${ids.length} record(s)?`
    );
    if (!ok) return;

    try {
      const r = await axios.post(
        `${API_BASE}/delete`,
        { vehicledetIds: ids },
        { headers: commonHeaders }
      );

      if (r.data?.success) {
        toast.success(r.data?.message || "Deleted successfully!");
        await loadList();
      } else {
        toast.error(r.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const getVisiblePages = () => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxButtons - 1;

    if (end > totalPages) {
      end = totalPages;
      start = end - maxButtons + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handlePageSizeChange = (e) => {
    const nextSize = Number(e.target.value) || DEFAULT_PAGE_SIZE;
    setPageSize(nextSize);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadMeta(selectedBranchId || branchId);
        await loadList();
      } catch (err) {
        console.error(err);
        toast.error("Failed to initialize page");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  if (!rights.canAccess) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700">
            You don’t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  const pagerBtnClass = (active = false, disabled = false) =>
    `min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold border transition-all duration-200 flex items-center justify-center ${
      disabled
        ? isDark
          ? "opacity-40 cursor-not-allowed border-gray-600 text-gray-400"
          : "opacity-40 cursor-not-allowed border-gray-300 text-gray-400"
        : active
        ? `${buttonClass} text-white border-transparent shadow`
        : isDark
        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
        : "border-gray-300 text-gray-700 hover:bg-white"
    }`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              HIRE VEHICLE DETAILS LISTING
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* HEADER */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Hire Vehicle Details Listing
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {loading ? "..." : `${totalRecords}`} Records
              </span>
              {selectedRows.length > 0 && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-red-900 text-red-100" : "bg-red-100 text-red-800"
                  }`}
                >
                  {selectedRows.length} Selected
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {rights.canAdd && branchActive === "1" && (
                <button
                  onClick={() => navigate("/vehicle/HireVehicle_DetailsEntry")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>
              )}

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* SEARCH CRITERIA */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={loadViewAll}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaList />
                    View All
                  </button>

                  <button
                    onClick={loadList}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    value={selectedBranchId}
                    onChange={onBranchChange}
                    disabled={branchType !== "HO"}
                    className={`${inputClass} ${
                      branchType !== "HO" ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <option value="">Select</option>
                    {branches.map((b, idx) => {
                      const id =
                        b?.fld_Branchid ||
                        b?.BranchId ||
                        b?.branchid ||
                        b?.fld_branchid ||
                        "";
                      const name =
                        b?.fld_BranchName ||
                        b?.BranchName ||
                        b?.branchname ||
                        id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={storeId}
                    onChange={(e) => setStoreId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {stores.map((s, idx) => {
                      const id = s?.fld_storeid || s?.StoreId || s?.storeid || "";
                      const name =
                        s?.fld_storename || s?.StoreName || s?.storename || id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Subtype</label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {vehicleTypes.map((v, idx) => {
                      const id =
                        v?.fld_vehicle_subtypeid ||
                        v?.VehicleSubtypeId ||
                        v?.subtypeid ||
                        v?.id ||
                        "";
                      const name =
                        v?.fld_vehicle_subtypename ||
                        v?.VehicleSubtypeName ||
                        v?.subtypename ||
                        v?.name ||
                        id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Hired From</label>
                  <input
                    type="text"
                    value={hiredFrom}
                    onChange={(e) => setHiredFrom(e.target.value)}
                    placeholder="Hired From"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={vehicleCode}
                    onChange={(e) => setVehicleCode(e.target.value)}
                    placeholder="Vehicle Code"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input
                    type="text"
                    value={vehicleRegNo}
                    onChange={(e) => setVehicleRegNo(e.target.value)}
                    placeholder="Vehicle Reg No"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={vehicleName}
                    onChange={(e) => setVehicleName(e.target.value)}
                    placeholder="Vehicle Name"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TABLE */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Code</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Reg No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Subtype</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Hired From</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Basis</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All on this page"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {paginatedRows.map((row, idx) => {
                      const id = Number(row.vehicleDetId);
                      const checked = selectedRows.includes(id);
                      const serial = pageStart + idx;

                      return (
                        <tr
                          key={id || serial}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {serial}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {row.vehicleCode}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                            {row.vehicleName}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.vehicleRegNo}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.vehicleSubtype}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.hiredFrom}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.basis}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                String(row.status).toLowerCase() === "open"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                              }`}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => onEdit(row)}
                              disabled={!(rights.canAdd && branchActive === "1")}
                              className={`text-yellow-500 hover:text-yellow-600 text-lg transition ${
                                !(rights.canAdd && branchActive === "1")
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {rights.canDelete && branchActive === "1" && (
                  <div className="p-4">
                    <button
                      onClick={onDeleteSelected}
                      disabled={selectedRows.length === 0}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                        selectedRows.length === 0
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      } bg-red-600 hover:bg-red-700 text-white`}
                    >
                      <FaTrash />
                      Remove {selectedRows.length > 0 ? `(${selectedRows.length})` : ""}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {!loading && totalRecords > 0 && (
            <div
              className={`mt-4 flex flex-col lg:flex-row items-center justify-between gap-4 px-2 py-3 ${
                isDark ? "text-gray-300" : "text-gray-700"
              }`}
            >
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <span>
                  Showing <span className="font-semibold">{pageStart}</span>–
                  <span className="font-semibold">{pageEnd}</span> of{" "}
                  <span className="font-semibold">{totalRecords}</span> records
                </span>

                <label className="flex items-center gap-2">
                  <span>Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={pagerBtnClass(false, currentPage === 1)}
                  title="Previous"
                >
                  <FaChevronLeft />
                </button>

                {getVisiblePages()[0] > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => goToPage(1)}
                      className={pagerBtnClass(currentPage === 1)}
                    >
                      1
                    </button>
                    {getVisiblePages()[0] > 2 && (
                      <span className="px-1 text-sm opacity-70">...</span>
                    )}
                  </>
                )}

                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => goToPage(page)}
                    className={pagerBtnClass(page === currentPage)}
                  >
                    {page}
                  </button>
                ))}

                {getVisiblePages().at(-1) < totalPages && (
                  <>
                    {getVisiblePages().at(-1) < totalPages - 1 && (
                      <span className="px-1 text-sm opacity-70">...</span>
                    )}
                    <button
                      type="button"
                      onClick={() => goToPage(totalPages)}
                      className={pagerBtnClass(currentPage === totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={pagerBtnClass(false, currentPage === totalPages)}
                  title="Next"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default HireVehicleDetailsListing;